/**
 * Loose ends — what in YNAB is worth a look, across every account at once.
 *
 * YNAB shows each of these one register at a time, so across nineteen accounts they
 * go unseen. Nothing here fixes anything: every row links to the place in YNAB where
 * it is fixed, because YNAB is the budget and this app only reads it.
 */

import { useQuery } from '@tanstack/react-query'
import { fetchLooseEnds, type LooseEndsResponse, queryKeys } from '../api.ts'
import { fmt, shortDate } from '../lib/format.ts'

export function LooseEnds() {
  const ends = useQuery({ queryKey: queryKeys.looseEnds, queryFn: fetchLooseEnds })
  const data = ends.data
  const clear =
    data &&
    data.connectionErrors.length === 0 &&
    data.unconfirmed.length === 0 &&
    data.unapproved.length === 0 &&
    data.uncategorised.length === 0

  return (
    <section className="flex max-w-[960px] flex-col gap-5 px-4 pt-[22px] pb-[60px] sm:px-7">
      <div className="flex flex-col gap-1.5">
        <h2 className="m-0 font-display text-[34px] font-normal leading-none">Loose ends</h2>
        <p className="m-0 max-w-[600px] text-[14px] text-muted">
          Things in YNAB worth a look, gathered from every account. Each one opens the account in
          YNAB, where it is fixed.
        </p>
      </div>

      {ends.isPending && <p className="text-[14px] text-muted">Looking…</p>}
      {ends.isError && (
        <p className="rounded-card bg-blush-soft p-4 text-[14px] text-blush-deep">
          {ends.error.message}
        </p>
      )}
      {clear && (
        <p className="rounded-card border border-sky-line bg-sky-soft px-5 py-4 text-[15px]">
          Nothing loose. Every connection is working and everything is approved and categorised.
        </p>
      )}

      {data && data.connectionErrors.length > 0 && (
        <Panel title="Bank connections in error" tone="alert">
          {data.connectionErrors.map((row) => (
            <Row key={row.accountId} url={row.url}>
              <span className="flex-1">{row.accountName}</span>
              <span className="text-blush-deep">Reconnect in YNAB</span>
            </Row>
          ))}
        </Panel>
      )}

      {data && data.unconfirmed.length > 0 && <Unconfirmed rows={data.unconfirmed} />}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {data && data.unapproved.length > 0 && (
          <Panel title="Waiting for approval">
            {data.unapproved.map((row) => (
              <Row key={row.accountId} url={row.url}>
                <span className="flex-1">{row.accountName}</span>
                <span className="text-ink-dim">{row.count}</span>
              </Row>
            ))}
          </Panel>
        )}
        {data && data.uncategorised.length > 0 && (
          <Panel title="Without a category">
            {data.uncategorised.map((row) => (
              <Row key={row.accountId} url={row.url}>
                <span className="flex-1">{row.accountName}</span>
                <span className="text-ink-dim">{row.count}</span>
              </Row>
            ))}
          </Panel>
        )}
      </div>
    </section>
  )
}

/**
 * Entries the bank never confirmed. The lookalikes are offered as a question, never a
 * conclusion: the same subscription lands every month for the same figure, so amount
 * and date alone cannot say two rows are one payment.
 */
function Unconfirmed({ rows }: { rows: LooseEndsResponse['unconfirmed'] }) {
  return (
    <Panel title="The bank never confirmed these">
      <p className="m-0 pb-1 text-[12.5px] text-muted">
        Entered in YNAB — by hand or from a schedule — on an account the bank imports into, and
        still uncleared a week or more later. Either the payment has not happened, or it happened at
        a different amount and YNAB imported it as a second row.
      </p>
      {rows.map((row) => (
        <div key={row.id} className="flex flex-col gap-1 border-t border-line-faint py-2">
          <a
            href={row.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 text-[13px] text-ink no-underline hover:underline"
          >
            <span className="w-[52px] text-muted">{shortDate(row.date)}</span>
            <span className="min-w-0 flex-1 truncate">
              {row.payee ?? 'No payee'}
              <span className="text-muted"> · {row.accountName}</span>
            </span>
            <span className="text-muted">{row.age} days</span>
            <span>{fmt(row.amount)}</span>
          </a>
          {row.lookalikes.map((other) => (
            <span key={other.id} className="pl-[64px] text-[12px] text-ink-dim">
              May be the same as {fmt(other.amount)} from {other.payee ?? 'no payee'} on{' '}
              {shortDate(other.date)}
              {other.approved ? '' : ', not yet approved'}.
            </span>
          ))}
        </div>
      ))}
    </Panel>
  )
}

function Panel({
  title,
  tone = 'plain',
  children,
}: {
  title: string
  tone?: 'plain' | 'alert'
  children: React.ReactNode
}) {
  return (
    <div
      className={`flex flex-col gap-1 rounded-card border px-4 py-3 ${
        tone === 'alert' ? 'border-blush bg-blush-soft' : 'border-line bg-card'
      }`}
    >
      <span className="pb-1 text-[13.5px] font-medium">{title}</span>
      {children}
    </div>
  )
}

function Row({ url, children }: { url: string; children: React.ReactNode }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 border-t border-line-faint py-1.5 text-[13px] text-ink no-underline first-of-type:border-t-0 hover:underline"
    >
      {children}
      <span aria-hidden className="text-muted">
        ↗
      </span>
    </a>
  )
}
